/**
 * Gateway event banners — transient UI for gateway-level events.
 *
 * Renders:
 * - Shutdown banner (server restarting)
 * - Gateway update available badge
 * - Exec approval request notifications
 * - Presence indicator (connected device count)
 */

import { useState, useEffect, useCallback } from 'react';
import type { ShutdownState, ExecApprovalRequest } from '../hooks/useGatewayEvents';
import type { PresenceEntry } from '../types/protocol';
import styles from './GatewayEventBanner.module.css';

// ── Shutdown Banner ──

interface ShutdownBannerProps {
  shutdown: ShutdownState;
  onDismiss: () => void;
}

export function ShutdownBanner({ shutdown, onDismiss }: ShutdownBannerProps) {
  const [countdown, setCountdown] = useState<number | null>(null);

  useEffect(() => {
    if (!shutdown.restartExpectedMs) return;

    const updateCountdown = () => {
      const elapsed = Date.now() - shutdown.receivedAt;
      const remaining = Math.max(0, shutdown.restartExpectedMs! - elapsed);
      setCountdown(Math.ceil(remaining / 1000));
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [shutdown.receivedAt, shutdown.restartExpectedMs]);

  return (
    <div className={styles.shutdownBanner} onClick={onDismiss}>
      <span className={styles.shutdownIcon}>🔄</span>
      <span className={styles.shutdownText}>
        Server restarting
        {shutdown.reason && shutdown.reason !== 'shutdown' && ` — ${shutdown.reason}`}
        {countdown !== null && countdown > 0 && ` (${countdown}s)`}
        {countdown === 0 && ' — reconnecting…'}
      </span>
    </div>
  );
}

// ── Gateway Update Badge ──

interface GatewayUpdateBadgeProps {
  currentVersion: string;
  latestVersion: string;
  onDismiss: () => void;
}

export function GatewayUpdateBadge({ currentVersion, latestVersion, onDismiss }: GatewayUpdateBadgeProps) {
  return (
    <div className={styles.updateBadge} onClick={onDismiss} title={`Current: ${currentVersion}`}>
      <span className={styles.updateIcon}>⬆️</span>
      <span className={styles.updateText}>
        Gateway {latestVersion} available
      </span>
    </div>
  );
}

// ── Exec Approval Notification ──

interface ExecApprovalNotificationProps {
  approvals: ExecApprovalRequest[];
}

export function ExecApprovalNotification({ approvals }: ExecApprovalNotificationProps) {
  const [expanded, setExpanded] = useState(false);

  if (approvals.length === 0) return null;

  return (
    <div
      className={styles.approvalBadge}
      onClick={() => setExpanded(!expanded)}
      title="Agent commands awaiting approval"
    >
      <span className={styles.approvalIcon}>⚠️</span>
      <span className={styles.approvalText}>
        {approvals.length} approval{approvals.length > 1 ? 's' : ''} pending
      </span>
      {expanded && (
        <div className={styles.approvalList}>
          {approvals.map((a) => (
            <div key={a.id} className={styles.approvalItem}>
              <code className={styles.approvalCommand}>{a.command}</code>
              <ExpiryTimer expiresAtMs={a.expiresAtMs} />
            </div>
          ))}
          <div className={styles.approvalHint}>
            Approve via CLI or another client
          </div>
        </div>
      )}
    </div>
  );
}

function ExpiryTimer({ expiresAtMs }: { expiresAtMs: number }) {
  const [remaining, setRemaining] = useState(() =>
    Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000)),
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setRemaining(Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(timer);
  }, [expiresAtMs]);

  if (remaining === 0) return <span className={styles.expiredLabel}>expired</span>;
  return <span className={styles.expiryTimer}>{remaining}s</span>;
}

// ── Presence Indicator ──

interface PresenceIndicatorProps {
  presence: PresenceEntry[];
}

export function PresenceIndicator({ presence }: PresenceIndicatorProps) {
  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = useCallback(() => setExpanded((p) => !p), []);

  // Don't show anything if alone (0 or 1 connection = just us)
  if (presence.length <= 1) return null;

  return (
    <div className={styles.presenceBadge} onClick={toggleExpanded}>
      <span className={styles.presenceIcon}>👥</span>
      <span className={styles.presenceCount}>{presence.length}</span>
      {expanded && (
        <div className={styles.presenceList}>
          {presence.map((entry, i) => (
            <div key={entry.instanceId ?? entry.deviceId ?? i} className={styles.presenceItem}>
              <span className={styles.presencePlatform}>
                {platformEmoji(entry.platform)} {entry.platform ?? 'unknown'}
              </span>
              {entry.version && (
                <span className={styles.presenceVersion}>v{entry.version}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function platformEmoji(platform?: string): string {
  if (!platform) return '💻';
  const p = platform.toLowerCase();
  if (p.includes('android')) return '📱';
  if (p.includes('ios') || p.includes('iphone') || p.includes('ipad')) return '📱';
  if (p.includes('mac')) return '💻';
  if (p.includes('web')) return '🌐';
  if (p.includes('linux')) return '🐧';
  if (p.includes('windows')) return '🪟';
  return '💻';
}
